import type { ReactNode } from 'react';
import { Box, Button, Grid, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { PageHeader } from 'src/ui/shell/PageHeader';

interface StatCardProps {
  title: string;
  value: string;
  hint: string;
  icon: ReactNode;
  trendLabel?: string;
}

function StatCard({ title, value, hint, icon, trendLabel }: StatCardProps) {
  return (
    <Paper
      sx={{
        p: 2.75,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        background:
          'linear-gradient(165deg, rgba(91,156,248,0.09) 0%, rgba(12,21,36,0.55) 42%, rgba(7,11,18,0.2) 100%)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, #5B9CF8, #9F8CFF)',
          opacity: 0.85,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography variant="body2" sx={{ opacity: 0.82, fontWeight: 700, textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}>
          {title}
        </Typography>
        <Box sx={{ color: 'primary.light', opacity: 0.95, display: 'flex' }}>{icon}</Box>
      </Stack>
      <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -0.04, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {trendLabel ? (
        <Typography variant="caption" sx={{ color: 'success.light', fontWeight: 600 }}>
          {trendLabel}
        </Typography>
      ) : null}
      <Typography variant="caption" sx={{ opacity: 0.62, lineHeight: 1.5 }}>
        {hint}
      </Typography>
    </Paper>
  );
}

const activity = [
  { label: 'Tenant provisioned', meta: 'acme-corp · 12m ago' },
  { label: 'Policy bundle published', meta: 'global · 42m ago' },
  { label: 'User directory sync', meta: 'all tenants · 1h ago' },
];

export function OverviewPage() {
  return (
    <Stack spacing={3.5}>
      <Paper
        sx={{
          p: { xs: 2.5, md: 3.5 },
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(125deg, rgba(91,156,248,0.14) 0%, rgba(12,21,36,0.92) 45%, rgba(7,11,18,0.95) 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            right: -40,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(159,140,255,0.35) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Stack spacing={2} sx={{ position: 'relative' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Box>
              <Typography variant="overline" sx={{ letterSpacing: 3, opacity: 0.75, fontWeight: 700 }}>
                x-IAM control plane
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, letterSpacing: -0.04, maxWidth: 560, lineHeight: 1.15 }}>
                Operate identity at enterprise scale
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.78, mt: 1.5, maxWidth: 640, lineHeight: 1.65 }}>
                One console for tenants, global users, and audit evidence across every platform x-IAM protects.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="contained" size="large" disabled startIcon={<DownloadOutlinedIcon />}>
                Export summary
              </Button>
              <Button variant="outlined" size="large" color="inherit" disabled>
                Schedule report
              </Button>
            </Stack>
          </Stack>
          <Box sx={{ pt: 1 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ opacity: 0.65, fontWeight: 600 }}>
                Platform readiness
              </Typography>
              <Typography variant="caption" sx={{ color: 'success.light', fontWeight: 700 }}>
                94%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={94}
              sx={{
                height: 8,
                borderRadius: 99,
                bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 99,
                  background: 'linear-gradient(90deg, #5B9CF8, #34D399)',
                },
              }}
            />
          </Box>
        </Stack>
      </Paper>

      <PageHeader
        eyebrow="Snapshot"
        title="Overview"
        description="Live posture across tenants and platforms. Connect these widgets to your x-IAM metrics and health endpoints."
        rightSlot={
          <Button variant="outlined" color="inherit" disabled sx={{ fontWeight: 600 }}>
            Refresh data
          </Button>
        }
      />

      <Grid container spacing={2.25}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Active tenants"
            value="12"
            trendLabel="+2 this quarter"
            hint="Provisioned and receiving traffic via x-IAM."
            icon={<ApartmentOutlinedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Directory users"
            value="48.2k"
            trendLabel="Stable week-over-week"
            hint="Unique principals across all tenants and apps."
            icon={<PeopleAltOutlinedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Security events (24h)"
            value="326"
            trendLabel="Within expected band"
            hint="Normalized audit events from the x-IAM stream."
            icon={<PolicyOutlinedIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Control plane"
            value="Healthy"
            hint="Synthetic checks — wire to mesh / load balancers."
            icon={<MonitorHeartOutlinedIcon />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.25}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2.75, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
              Operating model
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.82, lineHeight: 1.75, mb: 2 }}>
              Superadmin workflows stay deliberately narrow: tenant lifecycle, cross-tenant visibility for
              break-glass support, and immutable audit consumption. Day-to-day administration belongs in
              tenant-scoped consoles backed by the same x-IAM APIs.
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.72, lineHeight: 1.75 }}>
              When you connect the backend, replace placeholder counts with signed responses from the x-IAM
              service and enforce authentication at the edge before this UI ships to production operators.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.75, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
              Recent activity
            </Typography>
            <Stack spacing={2}>
              {activity.map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    pb: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    '&:last-of-type': { borderBottom: 'none', pb: 0 },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {row.label}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 0.5 }}>
                    {row.meta}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}
